/**
 * Modal SeletorConjuntos (subdivisao=3).
 *
 * Modal de selecao de conjuntos eolicos com:
 *   - Reuso do single-fetch de `getUsinasEolicas` (cache RTK Query
 *     compartilhado com SeletorUsinasEolicas — abrir um depois do outro
 *     nao bate o backend de novo).
 *   - Agregacao client-side via `agregarConjuntos`: ~1052 usinas reduzem
 *     a ~156 conjuntos com qtd_usinas e potencia_total_mw calculados.
 *   - Filtros client-side: busca textual + UF + Sub. SM + Sub. SH +
 *     Localizacao EPE.
 *   - Tabela virtualizada (`@tanstack/react-virtual`): mantida por
 *     uniformidade com `SeletorUsinasEolicas` mesmo a 156 linhas — o
 *     overhead e despreziivel e a sintaxe fica consistente entre as
 *     duas features.
 *   - Selecao multipla com `Set<number>` em useRef (toggle <16ms).
 *
 * Substitui o caminho `subdivisao === 3` do `SeletorEntidadesLegado`
 * (grid de checkboxes). Plugado via switch em `SeletorEntidades/index.tsx`.
 *
 * Por que NAO usamos React Query / react-table:
 *   O resto do app usa RTK Query (cache via tags) e renderiza tabelas
 *   manuais com array `COLUNAS`. Adicionar essas deps para um modal so
 *   romperia consistencia, criaria 2 sistemas de cache e adicionaria
 *   ~80KB gzip. A feature de usinas (SeletorUsinasEolicas) tomou a
 *   mesma decisao — espelhamos.
 *
 * Por que cache de 30min (mencionado no spec original) NAO e configurado
 * aqui: o RTK Query usa invalidacao por tag (`WindxUsinasEolicas`).
 * Cache so invalida quando o ETL roda — equivalente funcional ao
 * staleTime ilimitado, mais preciso. O endpoint `getUsinasEolicas`
 * ja providencia esse tag em `services/api/windx.tsx:188`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';

import { useGetUsinasEolicasQuery } from 'services/api/windx';
import {
  ConjuntoAgregado,
  OrdenacaoConjuntos,
  UsinaEolicaDenormalizada,
} from 'helper/windx';
import agregarConjuntos from 'helper/agregarConjuntos';
import filtrarConjuntos from 'helper/filtrarConjuntos';
import ordenarConjuntos from 'helper/ordenarConjuntos';
import useSelecaoConjuntos from 'hooks/Windx/useSelecaoConjuntos';
import useFiltrosConjuntos from 'hooks/Windx/useFiltrosConjuntos';
import {
  selectWindxIdsEntidades,
  selectWindxTelaAtiva,
  setIdsEntidades,
} from 'redux/reducers/windxControlPanelReducer';

import BarraDeFiltros from './BarraDeFiltros';
import ResumoSelecao from './ResumoSelecao';
import TabelaConjuntos from './TabelaConjuntos';

import './index.scss';

interface SeletorConjuntosProps {
  show: boolean;
  onHide: () => void;
}

function extrairUnicos<T>(
  itens: ConjuntoAgregado[],
  campo: (c: ConjuntoAgregado) => T | null,
): T[] {
  const set = new Set<T>();
  itens.forEach((c) => {
    const v = campo(c);
    if (v !== null && v !== undefined && v !== '') set.add(v);
  });
  return Array.from(set).sort((a, b) => String(a).localeCompare(String(b), 'pt-BR'));
}

export default function SeletorConjuntos({
  show,
  onHide,
}: SeletorConjuntosProps) {
  const dispatch = useDispatch();
  const idsSelecionadosRedux = useSelector(selectWindxIdsEntidades);
  const telaAtiva = useSelector(selectWindxTelaAtiva);
  // Mapeia 'comparativo' -> 'sm' para o filtro de subsistema (Comparativo
  // vive sob /windx/semanal — agregacao SM).
  const telaParaFiltro: 'sh' | 'sm' = telaAtiva === 'sh' ? 'sh' : 'sm';

  const usinasQuery = useGetUsinasEolicasQuery(undefined, { skip: !show });
  const usinas = useMemo<UsinaEolicaDenormalizada[]>(
    () => usinasQuery.data?.data ?? [],
    [usinasQuery.data],
  );

  const conjuntos = useMemo(() => agregarConjuntos(usinas), [usinas]);

  const filtrosHook = useFiltrosConjuntos();
  const selecao = useSelecaoConjuntos({ inicial: idsSelecionadosRedux });
  const [ordenacao, setOrdenacao] = useState<OrdenacaoConjuntos>({
    coluna: 'cod_conjunto',
    direcao: 'asc',
  });

  // Sincroniza buffer local com Redux quando o modal abre.
  useEffect(() => {
    if (show) {
      selecao.resetar(idsSelecionadosRedux);
      filtrosHook.limparFiltros();
    }
    // Intencional: omitir `selecao`/`filtrosHook` para nao resetar a cada
    // toggle interno.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const ufsDisponiveis = useMemo(
    () => extrairUnicos(conjuntos, (c) => c.uf_sigla),
    [conjuntos],
  );
  const subsistemasSmDisponiveis = useMemo(
    () => extrairUnicos(conjuntos, (c) => c.subsistema_sm_sigla),
    [conjuntos],
  );
  const subsistemasShDisponiveis = useMemo(
    () => extrairUnicos(conjuntos, (c) => c.subsistema_sh_sigla),
    [conjuntos],
  );
  const localizacoesEpeDisponiveis = useMemo(
    () => extrairUnicos(conjuntos, (c) => c.localizacao_epe),
    [conjuntos],
  );

  const conjuntosFiltrados = useMemo(
    () => filtrarConjuntos(conjuntos, filtrosHook.filtrosEfetivos),
    [conjuntos, filtrosHook.filtrosEfetivos],
  );
  const conjuntosOrdenados = useMemo(
    () => ordenarConjuntos(conjuntosFiltrados, ordenacao),
    [conjuntosFiltrados, ordenacao],
  );

  const conjuntosPorId = useMemo(() => {
    const m = new Map<number, ConjuntoAgregado>();
    conjuntos.forEach((c) => m.set(c.id, c));
    return m;
  }, [conjuntos]);

  const totalSelecionados = selecao.count;
  const { totalUsinas, totalPotenciaMw } = selecao.totalAgregado(conjuntosPorId);

  const handleAplicar = () => {
    // 2026-05-29: apos aplicar a selecao, NAO dispara realizarConsulta
    // automaticamente. setIdsEntidades ja marca consultaPendente=true; o
    // usuario decide quando recarregar pelo botao "Realizar Consulta" do
    // painel — janela necessaria para que ele leia o aviso de complexidade
    // (MedidorComplexidade) antes de enviar a query. Mesmo padrao adotado
    // no ModalCadastrar de simulacoes antigas (decisao PO 2026-05-26).
    dispatch(setIdsEntidades(selecao.ids));
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      scrollable
      className="windx-seletor-conjuntos-modal"
      data-testid="windx-seletor-conjuntos-modal"
      aria-labelledby="windx-seletor-conjuntos-titulo"
    >
      <Modal.Header closeButton>
        <Modal.Title id="windx-seletor-conjuntos-titulo" className="h6 mb-0">
          Selecionar conjuntos
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <>
          {usinasQuery.isLoading && (
            <div className="p-3 text-center">
              <Spinner animation="border" role="status" size="sm" />
              <span className="ms-2">Carregando conjuntos...</span>
            </div>
          )}
          {usinasQuery.error && (
            <Alert variant="danger" className="m-0">
              Não foi possível carregar os conjuntos. Tente novamente em instantes.
            </Alert>
          )}
          {usinasQuery.data && (
            <div className="windx-seletor-conjuntos">
              <BarraDeFiltros
                busca={filtrosHook.filtros.busca}
                onBuscaChange={filtrosHook.setBusca}
                ufsDisponiveis={ufsDisponiveis}
                ufsSelecionadas={filtrosHook.filtros.ufs}
                onUfsChange={filtrosHook.setUfs}
                subsistemasSmDisponiveis={subsistemasSmDisponiveis}
                subsistemasSmSelecionados={filtrosHook.filtros.subsistemasSm}
                onSubsistemasSmChange={filtrosHook.setSubsistemasSm}
                subsistemasShDisponiveis={subsistemasShDisponiveis}
                subsistemasShSelecionados={filtrosHook.filtros.subsistemasSh}
                onSubsistemasShChange={filtrosHook.setSubsistemasSh}
                localizacoesEpeDisponiveis={localizacoesEpeDisponiveis}
                localizacoesEpeSelecionadas={filtrosHook.filtros.localizacoesEpe}
                onLocalizacoesEpeChange={filtrosHook.setLocalizacoesEpe}
                tela={telaParaFiltro}
              />
              <TabelaConjuntos
                conjuntos={conjuntosOrdenados}
                isSelecionado={selecao.isSelecionado}
                onToggle={selecao.toggle}
                onSelecionarVisiveis={selecao.selecionarTodos}
                onDesselecionarVisiveis={selecao.desselecionarTodos}
                ordenacao={ordenacao}
                onOrdenacaoChange={setOrdenacao}
              />
              <ResumoSelecao
                selecionados={totalSelecionados}
                total={conjuntos.length}
                totalFiltrados={conjuntosOrdenados.length}
                totalUsinas={totalUsinas}
                totalPotenciaMw={totalPotenciaMw}
              />
            </div>
          )}
        </>
      </Modal.Body>
      <Modal.Footer>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={selecao.limpar}
          disabled={totalSelecionados === 0}
        >
          Limpar
        </Button>
        <Button size="sm" onClick={handleAplicar}>
          Aplicar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
